/**
 * Canvas 战场渲染器
 *
 * 职责：
 *   - 把 BattleState（self + enemy 1v1 视角）渲染到 <canvas>
 *   - 25×25 demo override（设计稿 16×16）
 *   - 4 地块色值（empty/obstacle/cover/spawn）
 *   - A/B/FIRE 三色（青蓝 #4EC9B0 / 朱红 #F25C54 / 橘黄 #FFA630）
 *   - 击杀动效（HP 归零时白闪 200ms）
 *
 * 性能预算：
 *   - 30Hz 同步 tick，每帧 < 8ms（剩余 25ms 给 Pyodide think()）
 *
 * 2026-07-05 FR-021 改造：
 *   - 加 token 适配层（getComputedStyle 读 :root Ardot 变量）
 *   - drawTank 改用 svgLoader 引用 tank-team-a/b SVG（FR-020 §2.1 锐角方块）
 *   - COLORS 保留作 fallback（@deprecated）
 */

import type { BattleState, BotAction, Position, TileType, VfxFrameEvents } from "../engine/types";
import { TANK_STATS } from "../engine/tank-stats";
import { svgLoader, type SvgKey } from "../utils/svg-loader";

// 实色高对比兜底（2026-07-28 V15 午夜卫星图色板）
const COLORS = {
  bg: "#0A1626",              // 地图底图(午夜蓝)
  empty: "#0A1626",           // 空地(=背景，不填色)
  obstacle: "transparent",    // 障碍(不填底色，只画标记)
  cover: "transparent",       // 掩体(不填底色，只画标记)
  spawn: "#0A1626",           // 出生点(用 mapSpA/mapSpB 区分)
  unreachable: "rgba(184,84,86,0.15)", // 不可达(暗红半透明)
  teamA: "#4A9BB8",           // 钢青（玩家）
  teamB: "#B85456",           // 暗红（AI）
  fire: "#C4A058",            // 暗琥珀（开火特效）
  text: "#D4DCE8",            // 主文字(稍亮,适配午夜蓝底)
  textDim: "#5A768A",         // 次文字
  gridLine: "rgba(74,155,184,0.08)",    // 点阵网格(钢青点)
  // 实色高对比新增
  mapGridMajor: "rgba(74,155,184,0.12)", // 5×5 主网格(钢青微透)
  mapMidline: "rgba(74,155,184,0.20)",   // 中线虚线(钢青)
  mapSpA: "#4A9BB8",          // 队A出生点(钢青)
  mapSpB: "#B85456",          // 队B出生点(暗红)
  mapCoverStroke: "#4A9BB8",  // 掩体描边(钢青)
  mapObstacleStroke: "#8B4A4A", // 障碍斜纹(暗红实色)
} as const;

/**
 * Ardot token → COLORS 键的映射
 * 屏 4 启动时 canvas-renderer 构造读 :root CSS 变量
 * V14 地图提亮：映射到 --map-* token（独立于深色/亮色主题切换）
 * 找不到时 fallback 到 COLORS 硬值
 */
const TOKEN_TO_COLORS: Record<keyof typeof COLORS, string> = {
  bg: "--map-bg",
  empty: "--map-empty",
  obstacle: "--map-obstacle",
  cover: "--map-cover",
  spawn: "--map-empty",
  unreachable: "--map-unreachable",
  teamA: "--tactical-cyan",
  teamB: "--valorant-red",
  fire: "--tactical-amber",
  text: "--text-primary",
  textDim: "--map-midline",
  gridLine: "--map-grid-fine",
  mapGridMajor: "--map-grid-major",
  mapMidline: "--map-midline",
  mapSpA: "--map-spawn-a",
  mapSpB: "--map-spawn-b",
  mapCoverStroke: "--map-cover-stroke",
  mapObstacleStroke: "--map-obstacle-stroke",
};

export interface RenderOptions {
  cellSize?: number;
  showGrid?: boolean;
  showHpBar?: boolean;
  showSpawns?: boolean;
  deathFlashMs?: number;
  /** VFX toggles — 玩家可开关的特效 */
  showVisionRange?: boolean;
  showDamageNumbers?: boolean;
  showScreenShake?: boolean;
  showHitPause?: boolean;
  showProjectiles?: boolean;
  showFragments?: boolean;
  showHud?: boolean;
  showActionLines?: boolean;
  showKillFeed?: boolean;
}

interface FlashState {
  startTime: number;
  team: "team_a" | "team_b";
}

/** V3: 开火爆点事件 */
interface FireEvent { tick: number; target: Position; shooterTeam: "team_a" | "team_b"; }
/** V3: 命中爆点事件 */
interface HitEvent { tick: number; target: Position; targetTeam: "team_a" | "team_b"; }
/** V3: 攻击线事件 */
interface ShootLine { tick: number; from: Position; to: Position; team: "team_a" | "team_b"; }

// == V14: 动效队列类型 ===

/** 伤害数字动画（Canvas 等宽字体上浮淡出） */
interface DamageNumberAnim {
  pos: Position;         // 世界坐标
  value: number;
  frameStart: number;    // render tick 序号
  duration: number;      // 总帧数（15 = 0.5s @ 30fps）
  team: "team_a" | "team_b";
}

/** 弹丸飞行动画 */
interface ProjectileAnim {
  from: Position;
  to: Position;
  team: "team_a" | "team_b";
  tickStart: number;
  duration: number;      // 渲染帧数
}

/** 锐角碎片粒子 */
interface FragmentParticle {
  x: number;             // 像素坐标
  y: number;
  vx: number;            // 像素/帧
  vy: number;
  size: number;          // 4-8px
  alpha: number;
  life: number;          // 剩余帧数
}

/** 屏幕震动状态 */
interface ShakeState {
  active: boolean;
  frameStart: number;
  duration: number;      // 帧数
  intensity: number;     // px
}

/** 命中停顿状态 */
interface HitStopState {
  active: boolean;
  frameStart: number;
  duration: number;      // 帧数
}

export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private opts: Required<RenderOptions>;
  /** V3: 多坦克死亡白闪队列（替代 1v1 单例） */
  private deathFlashes: FlashState[] = [];
  /** V3: 多坦克开火爆点队列 */
  private fireEvents: FireEvent[] = [];
  /** V3: 多坦克命中爆点队列 */
  private hitEvents: HitEvent[] = [];
  /** V3: 多坦克攻击线队列 */
  private shootLines: ShootLine[] = [];
  /** FR-022 P2:屏 4 启动时 setCoverPositions 传入,drawCallouts 画 24 个补给标签 */
  private coverPositions: Array<[number, number]> = [];
  // V14: 动效队列
  private damageNumbers: DamageNumberAnim[] = [];
  private projectiles: ProjectileAnim[] = [];
  private fragments: FragmentParticle[] = [];
  private shake: ShakeState = { active: false, frameStart: 0, duration: 0, intensity: 0 };
  private hitStop: HitStopState = { active: false, frameStart: 0, duration: 0 };
  private rAFTick = 0; // 独立渲染帧计数（不使用 state.tick）
  private tiles: TileType[] | null = null;
  /** 当前地图尺寸（每帧 render() 更新），tileToSvgKey / drawTile 消费 */
  private mapSize: { width: number; height: number } = { width: 25, height: 25 };
  /** V15(2026-07-28)：完整出生点列表（1v1 也有 2 个 spawn 区域，画所有） */
  private spawnPositions: { team_a: Array<{ x: number; y: number }>; team_b: Array<{ x: number; y: number }> } = { team_a: [], team_b: [] };
  /** 2026-07-05 FR-021：Ardot token 真色值（构造时读 :root），开放 string 键 */
  private tokens: Record<string, string> = {};

  constructor(canvas: HTMLCanvasElement, opts: RenderOptions = {}) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context not available");
    this.ctx = ctx;
    this.opts = {
      cellSize: opts.cellSize ?? 32,
      showGrid: opts.showGrid ?? false,
      showHpBar: opts.showHpBar ?? true,
      showSpawns: opts.showSpawns ?? true,
      deathFlashMs: opts.deathFlashMs ?? 200,
      showVisionRange: opts.showVisionRange ?? true,
      showDamageNumbers: opts.showDamageNumbers ?? true,
      showScreenShake: opts.showScreenShake ?? true,
      showHitPause: opts.showHitPause ?? true,
      showProjectiles: opts.showProjectiles ?? true,
      showFragments: opts.showFragments ?? true,
      showHud: opts.showHud ?? true,
      showActionLines: opts.showActionLines ?? true,
      showKillFeed: opts.showKillFeed ?? true,
    };
    this.readArdotTokens();
  }

  /**
   * 读 :root 上的 Ardot CSS 变量，存为 tokens
   * canvas API 拿不到 CSS var，必须走 getComputedStyle
   * 读取失败时保持空对象，getColor() fallback 到 COLORS
   */
  private readArdotTokens(): void {
    if (typeof document === "undefined") return;
    const styles = getComputedStyle(document.documentElement);
    for (const [key, cssVar] of Object.entries(TOKEN_TO_COLORS) as [keyof typeof COLORS, string][]) {
      const value = styles.getPropertyValue(cssVar).trim();
      if (value) {
        this.tokens[key] = value;
      }
    }
    // V14 地图提亮: 额外读 map-* token（TOKEN_TO_COLORS 已包含基础映射，此处补充）
    const mapCoverStroke = styles.getPropertyValue("--map-cover-stroke").trim();
    if (mapCoverStroke) this.tokens.map_cover_stroke = mapCoverStroke;
    const mapObstacleStroke = styles.getPropertyValue("--map-obstacle-stroke").trim();
    if (mapObstacleStroke) this.tokens.map_obstacle_stroke = mapObstacleStroke;
    const mapSpawnAStroke = styles.getPropertyValue("--map-spawn-a-stroke").trim();
    if (mapSpawnAStroke) this.tokens.map_spawn_a_stroke = mapSpawnAStroke;
    const mapSpawnBStroke = styles.getPropertyValue("--map-spawn-b-stroke").trim();
    if (mapSpawnBStroke) this.tokens.map_spawn_b_stroke = mapSpawnBStroke;
    // FR-029: text-secondary / tactical-cyan-hi（Day 高对比度用途）
    const textSecondary = styles.getPropertyValue("--text-secondary").trim();
    if (textSecondary) this.tokens.text_secondary = textSecondary;
    const cyanHi = styles.getPropertyValue("--tactical-cyan-hi").trim();
    if (cyanHi) this.tokens.cyan_hi = cyanHi;
  }

  /**
   * FR-029: 主题切换时刷新所有 token 值
   * 屏 4 battle 在收到 theme-change 事件后调用
   */
  refreshTokens(): void {
    this.tokens = {};
    this.readArdotTokens();
  }

  /**
   * 取得色值（优先 token，fallback 旧 COLORS）
   * 全 canvas-renderer 的色值都走这个方法（保证单一来源）
   */
  private getColor(key: keyof typeof COLORS): string {
    return this.tokens[key] ?? COLORS[key];
  }

  setOptions(opts: Partial<RenderOptions>) {
    this.opts = { ...this.opts, ...opts };
  }

  /** V3: 死亡白闪入队（多坦克模式支持同 tick 多坦克阵亡） */
  flashDeath(team: "team_a" | "team_b") {
    this.deathFlashes.push({ startTime: performance.now(), team });
  }

  /** V3: 开火爆点入队 */
  recordFire(tick: number, target: Position, shooterTeam: "team_a" | "team_b") {
    this.fireEvents.push({ tick, target, shooterTeam });
  }

  /** V3: 命中爆点入队（5 tick 衰减 + scale 放大） */
  recordHit(tick: number, target: Position, targetTeam: "team_a" | "team_b") {
    this.hitEvents.push({ tick, target, targetTeam });
  }

  /** V3: 攻击线入队（8 tick 衰减画线） */
  recordShoot(tick: number, from: Position, to: Position, team: "team_a" | "team_b") {
    this.shootLines.push({ tick, from, to, team });
  }

  // === V14: 动效事件聚合入口 ===

  /** 批量记录一帧所有动效事件（由屏 4 render loop 调用） */
  recordVfxEvents(events: VfxFrameEvents, tick: number): void {
    // 伤害数字
    for (const d of events.damage) {
      this.damageNumbers.push({ pos: d.targetPos, value: d.damage, frameStart: this.rAFTick, duration: 15, team: d.targetPos === d.targetPos ? "team_b" : "team_a" });
    }
    // 弹丸飞行
    for (const s of events.shoots) {
      this.projectiles.push({ from: s.from, to: s.to, team: s.shooterTeam, tickStart: tick, duration: 6 });
    }
    // 击杀碎片 + 震动
    for (const k of events.kills) {
      this.spawnKillFragments(k.pos);
      this.shake = { active: true, frameStart: this.rAFTick, duration: 5, intensity: 4 };
    }
    // 命中停顿（有伤害事件就触发）
    if (events.damage.length > 0) {
      this.hitStop = { active: true, frameStart: this.rAFTick, duration: 4 };
      if (this.shake.intensity < 3) {
        this.shake = { active: true, frameStart: this.rAFTick, duration: 5, intensity: Math.min(3, events.damage.length * 2) };
      }
    }
  }

  /** 清理过期动效对象（每帧调用） */
  private cleanupExpiredAnims(): void {
    const now = this.rAFTick;
    this.damageNumbers = this.damageNumbers.filter(d => now - d.frameStart < d.duration);
    this.projectiles = this.projectiles.filter(p => now - p.tickStart < p.duration);
    this.fragments = this.fragments.filter(f => f.life > 0);
  }

  /** 在击杀位置生成碎片粒子 */
  private spawnKillFragments(pos: Position): void {
    const cx = pos.x * this.opts.cellSize + this.opts.cellSize / 2;
    const cy = pos.y * this.opts.cellSize + this.opts.cellSize / 2;
    const count = 8 + Math.floor(Math.random() * 3); // 8-10 块
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 2 + Math.random() * 3;
      this.fragments.push({
        x: cx, y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 4 + Math.random() * 4,
        alpha: 1,
        life: 9, // 300ms @ 30fps
      });
    }
  }

  loadMap(tiles: TileType[]) {
    this.tiles = tiles;
  }

  /**
   * FR-022 P2:屏 4 启动时传入 cover positions,drawCallouts 在每个 cover 位置画 supply 标签
   * 来自 map-utils.extractCoverPositions()(FR-018 ADR-018.8)
   */
  setCoverPositions(positions: Array<[number, number]>): void {
    this.coverPositions = positions;
  }

  /**
   * V15(2026-07-28): 屏 4 启动时传入完整出生点列表（1v1 也有 2 个 spawn 区域）
   * drawSpawnsMulti 在 1v1 模式下用此列表画所有出生点（state.self.spawn 只是第一个）
   */
  setSpawnPositions(spawns: { team_a: Array<{ x: number; y: number }>; team_b: Array<{ x: number; y: number }> }): void {
    this.spawnPositions = spawns;
  }

  /** V14: 实时更新单个渲染选项（VFX 开关面板用） */
  setOption<K extends keyof RenderOptions>(key: K, value: NonNullable<RenderOptions[K]>): void {
    (this.opts as Record<K, NonNullable<RenderOptions[K]>>)[key] = value;
  }

  render(state: BattleState, playerAction?: BotAction, aiAction?: BotAction) {
    const { ctx, opts } = this;
    const { width, height } = state.map_size;
    const cell = opts.cellSize;
    this.mapSize = { width, height };

    // V14: 独立渲染帧计数 + 清理过期动效
    this.rAFTick++;
    this.cleanupExpiredAnims();

    // V14: Hit Stop — 命中缩帧，跳过绘制但帧计数不跳过
    if (opts.showHitPause && this.hitStop.active) {
      if (this.rAFTick - this.hitStop.frameStart < this.hitStop.duration) return;
      this.hitStop.active = false;
    }

    ctx.save();

    // V14: Screen Shake（受击震动）
    if (opts.showScreenShake) {
      this.applyShake(ctx, width, height, cell);
    }

    // 1. 背景
    ctx.fillStyle = this.getColor("bg");
    ctx.fillRect(0, 0, width * cell, height * cell);

    // 2. 地形
    if (this.tiles) {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          this.drawTile(x, y, this.tiles[y * width + x]);
        }
      }
    }

    // 3. 网格（可选）— 简化版：5×5 主网格线 + 中线虚线 + 4 角硬边框
    // (2026-07-28 去掉每交叉点的 1px 圆点 — 低分辨率下会渲染成噪点 L 形)
    if (opts.showGrid) {
      // 主网格线：每 5 格画细实线
      ctx.strokeStyle = this.getColor("mapGridMajor");
      ctx.lineWidth = 1;
      for (let i = 0; i <= width; i++) {
        if (i % 5 === 0) {
          ctx.beginPath();
          ctx.moveTo(i * cell, 0);
          ctx.lineTo(i * cell, height * cell);
          ctx.stroke();
        }
      }
      for (let i = 0; i <= height; i++) {
        if (i % 5 === 0) {
          ctx.beginPath();
          ctx.moveTo(0, i * cell);
          ctx.lineTo(width * cell, i * cell);
          ctx.stroke();
        }
      }
      // 中线虚线
      ctx.strokeStyle = this.getColor("mapMidline");
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      const midX = Math.floor(width / 2) * cell;
      ctx.beginPath();
      ctx.moveTo(midX, 0);
      ctx.lineTo(midX, height * cell);
      ctx.stroke();
      const midY = Math.floor(height / 2) * cell;
      ctx.beginPath();
      ctx.moveTo(0, midY);
      ctx.lineTo(width * cell, midY);
      ctx.stroke();
      ctx.setLineDash([]);
      // 4 角硬边框 (外圈 1px 实线)
      ctx.strokeStyle = this.getColor("mapGridMajor");
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      ctx.strokeRect(0, 0, width * cell, height * cell);
    }

    // 4. 出生点标记 — V3: 支持多 spawn
    if (opts.showSpawns) {
      this.drawSpawnsMulti(cell, state);
    }

    // 5. 坦克 — V3: 多坦克模式遍历 team_a/team_b
    const isMulti = !!(state.team_a && state.team_b && state.team_a.tanks.length > 1);
    if (isMulti) {
      // V3: 多坦克绘制 — 遍历所有坦克
      const pActions = (state as any)._renderActionsA as BotAction[] | undefined;
      const aActions = (state as any)._renderActionsB as BotAction[] | undefined;
      for (let i = 0; i < state.team_a!.tanks.length; i++) {
        const tank = state.team_a!.tanks[i];
        this.drawTank(tank.position, "team_a", tank.health, tank.max_health, tank.is_alive, pActions?.[i]);
      }
      for (let i = 0; i < state.team_b!.tanks.length; i++) {
        const tank = state.team_b!.tanks[i];
        this.drawTank(tank.position, "team_b", tank.health, tank.max_health, tank.is_alive, aActions?.[i]);
      }
    } else {
      // 1v1 向后兼容
      this.drawTank(state.self.position, "team_a", state.self.health, state.self.max_health, state.self.is_alive, playerAction);
      this.drawTank(state.enemy.position, "team_b", state.enemy.health, state.enemy.max_health, state.enemy.is_alive, aiAction);
    }

    // 6. 开火特效 — V3: 遍历所有 fire 事件
    const nowTick = state.tick;
    this.fireEvents = this.fireEvents.filter((e) => {
      const elapsed = nowTick - e.tick;
      if (elapsed < 0 || elapsed >= 5) return false;
      const alpha = 1 - elapsed / 5;
      const fx = e.target.x * cell;
      const fy = e.target.y * cell;
      const fireKey: SvgKey = e.shooterTeam === "team_a" ? "fire-blast-a" : "fire-blast-b";
      if (svgLoader.has(fireKey)) {
        ctx.save();
        ctx.globalAlpha = alpha;
        svgLoader.draw(ctx, fireKey, { x: fx, y: fy, dw: cell, dh: cell });
        ctx.restore();
      } else {
        const alphaHex = Math.floor(alpha * 255).toString(16).padStart(2, "0");
        ctx.fillStyle = `${this.getColor("fire")}${alphaHex}`;
        ctx.fillRect(fx + cell / 4, fy + cell / 4, cell / 2, cell / 2);
      }
      return true;
    });

    // 7. 命中爆点 — V3: 遍历所有 hit 事件
    this.drawAllHits(nowTick, cell);

    // 8. Callout 装饰 — V3: 多坦克模式简化（不画 callout 避免遮挡）
    if (opts.showHud && !isMulti) {
      this.drawCallouts(state, cell);
    }

    // 9. HUD 4 角
    if (opts.showHud) {
      this.drawCorners(width * cell, height * cell);
    }

    // 10. 视野圈 — V3: 多坦克遍历
    if (opts.showVisionRange) {
      this.drawVisionCirclesMulti(state, cell, isMulti);
    }

    // 11. 攻击线 — V3: 遍历所有 shoot 事件
    if (opts.showActionLines) {
      this.drawAllActionLines(nowTick, cell);
    }

    // == V14: 新动效绘制层 ==

    // 12. 弹丸飞行
    if (opts.showProjectiles) {
      this.drawProjectiles(nowTick, cell);
    }

    // 13. 碎片粒子（命中/击杀）
    if (opts.showFragments) {
      this.drawFragments();
    }

    // 14. 伤害数字
    if (opts.showDamageNumbers) {
      this.drawDamageNumbers(cell);
    }

    ctx.restore();
  }

  private drawTile(x: number, y: number, tile: TileType) {
    const { ctx, opts } = this;
    const cell = opts.cellSize;
    const px = x * cell;
    const py = y * cell;

    // FR-022 P0:5 种瓦片切 SVG（已加载走 SVG;未加载走 hardcode 兜底）
    // spawn 瓦片根据 x 坐标区分 a/b 队（x=0 → team_a，x=width-1 → team_b）
    const tileKey = this.tileToSvgKey(tile, x);
    if (tileKey && svgLoader.has(tileKey)) {
      svgLoader.draw(ctx, tileKey, { x: px, y: py, dw: cell, dh: cell });
      // P1: 中央 obstacle 视觉差异化（加粗 X + 半透光叠加层，暗示主战场障碍）
      if (tile === "obstacle" && this.isCenterObstacle(x, y)) {
        ctx.save();
        ctx.strokeStyle = "rgba(200, 60, 60, 0.35)";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(px + cell * 0.2, py + cell * 0.2);
        ctx.lineTo(px + cell * 0.8, py + cell * 0.8);
        ctx.moveTo(px + cell * 0.8, py + cell * 0.2);
        ctx.lineTo(px + cell * 0.2, py + cell * 0.8);
        ctx.stroke();
        ctx.restore();
      }
      return;
    }

    // ===== fallback：实色高对比 4 种瓦片（SVG 未加载时兜底）=====
    // 空地：不填色（底色即背景）
    // 障碍：红色斜纹 + 暗红边框
    // 掩体：青色角括号 + 中心菱形
    // 不可达：红色半透底 + X
    if (tile === "empty") return;

    if (tile === "obstacle") {
      ctx.save();
      ctx.strokeStyle = this.getColor("mapObstacleStroke");
      ctx.lineWidth = 1.2;
      const inset = cell * 0.15;
      const gap = cell * 0.12;
      for (let k = -1; k <= 1; k++) {
        const off = k * gap;
        ctx.beginPath();
        ctx.moveTo(px + inset + off, py + inset);
        ctx.lineTo(px + cell - inset + off, py + cell - inset);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(px + cell - inset + off, py + inset);
        ctx.lineTo(px + inset + off, py + cell - inset);
        ctx.stroke();
      }
      ctx.strokeStyle = "#C43E3E";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(px + 1, py + 1, cell - 2, cell - 2);
      ctx.restore();
      return;
    }

    if (tile === "cover") {
      ctx.save();
      ctx.strokeStyle = this.getColor("mapCoverStroke");
      ctx.lineWidth = 1.5;
      const m = cell * 0.28;
      ctx.beginPath();
      ctx.moveTo(px + m, py + 2);
      ctx.lineTo(px + 2, py + 2);
      ctx.lineTo(px + 2, py + m);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(px + cell - m, py + cell - 2);
      ctx.lineTo(px + cell - 2, py + cell - 2);
      ctx.lineTo(px + cell - 2, py + cell - m);
      ctx.stroke();
      const cx = px + cell / 2;
      const cy = py + cell / 2;
      const d = cell * 0.15;
      ctx.beginPath();
      ctx.moveTo(cx, cy - d);
      ctx.lineTo(cx + d, cy);
      ctx.lineTo(cx, cy + d);
      ctx.lineTo(cx - d, cy);
      ctx.closePath();
      ctx.fillStyle = this.getColor("mapCoverStroke");
      ctx.fill();
      ctx.restore();
      return;
    }

    if (tile === "unreachable") {
      ctx.fillStyle = this.getColor("unreachable");
      ctx.fillRect(x * cell, y * cell, cell, cell);
      ctx.strokeStyle = this.getColor("unreachable");
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x * cell + 4, y * cell + 4);
      ctx.lineTo(x * cell + cell - 4, y * cell + cell - 4);
      ctx.moveTo(x * cell + cell - 4, y * cell + 4);
      ctx.lineTo(x * cell + 4, y * cell + cell - 4);
      ctx.stroke();
      return;
    }

    if (tile === "spawn") {
      // 极浅底色 — 仅地形示意；主力标记由 drawSpawnsMulti 负责
      const isA = x < this.mapSize.width / 2;
      ctx.fillStyle = isA ? this.getColor("mapSpA") : this.getColor("mapSpB");
      ctx.globalAlpha = 0.08;
      ctx.fillRect(x * cell, y * cell, cell, cell);
      ctx.globalAlpha = 1;
    }
  }

  /**
   * FR-022 P0：TileType → SvgKey 映射
   * spawn 区分 a/b 队（x < mapWidth/2 → spawn-a，其余 → spawn-b）
   * 返回 null 时走 hardcode fallback
   */
  private tileToSvgKey(tile: TileType, x: number): SvgKey | null {
    switch (tile) {
      case "empty":
        return "tile-empty";
      case "obstacle":
        return "tile-obstacle";
      case "cover":
        return "tile-cover";
      case "spawn":
        return x < this.mapSize.width / 2 ? "tile-spawn-a" : "tile-spawn-b";
      case "unreachable":
        return "tile-unreachable";
    }
  }

  /** P1: 判断瓦片是否在战场中央区域（切比雪夫距离 ≤ 地图尺寸 20%），中央障碍需视觉差异化 */
  private isCenterObstacle(x: number, y: number): boolean {
    const cx = Math.floor(this.mapSize.width / 2);
    const cy = Math.floor(this.mapSize.height / 2);
    const maxDim = Math.max(this.mapSize.width, this.mapSize.height);
    return Math.max(Math.abs(x - cx), Math.abs(y - cy)) <= maxDim * 0.2;
  }

  /**
   * V3: 多坦克 spawn 绘制 — 遍历 team_a/team_b 的所有 spawn
   */
  private drawSpawnsMulti(cell: number, state: BattleState): void {
    const { ctx } = this;
    const hasSVG = svgLoader.has("spawn-marker-a") && svgLoader.has("spawn-marker-b");

    const drawSpawn = (x: number, y: number, team: "team_a" | "team_b") => {
      // 检测 drawTile 是否已在此位置渲染了 spawn 瓦片（避免双重 fill+border）
      const tileAtPos = this.tiles && this.mapSize ? this.tiles[Math.floor(y) * this.mapSize.width + Math.floor(x)] : null;
      const hasSpawnTile = tileAtPos === "spawn";

      if (hasSVG) {
        const key = team === "team_a" ? "spawn-marker-a" : "spawn-marker-b";
        svgLoader.draw(ctx, key, { x: x * cell, y: y * cell, dw: cell, dh: cell });
        return;
      }

      // drawTile 未在此渲染 spawn → 画完整标记（fill + border + 三角）
      if (!hasSpawnTile) {
        ctx.fillStyle = team === "team_a" ? this.getColor("mapSpA") : this.getColor("mapSpB");
        ctx.globalAlpha = 0.3;
        ctx.fillRect(x * cell, y * cell, cell, cell);
        ctx.globalAlpha = 1;
        ctx.strokeStyle = team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x * cell + 1, y * cell + 1, cell - 2, cell - 2);
      }

      // 朝向三角（队A朝右，队B朝左）— 始终叠加
      const cx = x * cell + cell / 2;
      const cy = y * cell + cell / 2;
      const sz = cell * 0.18;
      ctx.fillStyle = team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
      ctx.beginPath();
      if (team === "team_a") {
        ctx.moveTo(cx, cy - sz);
        ctx.lineTo(cx + sz, cy);
        ctx.lineTo(cx, cy + sz);
      } else {
        ctx.moveTo(cx, cy - sz);
        ctx.lineTo(cx - sz, cy);
        ctx.lineTo(cx, cy + sz);
      }
      ctx.closePath();
      ctx.fill();
    };

    // V3: 多坦克从 team_a/team_b 取 spawn
    if (state.team_a && state.team_b) {
      for (const tank of state.team_a.tanks) {
        drawSpawn(tank.spawn.x, tank.spawn.y, "team_a");
      }
      for (const tank of state.team_b.tanks) {
        drawSpawn(tank.spawn.x, tank.spawn.y, "team_b");
      }
    } else {
      // 1v1: 用 spawnPositions 画所有出生点（state.self.spawn 只是 spawnPositions[0]）
      // battle_easy 1v1 也有 2 个 spawn 区域（y=12, y=13），不能漏画
      const spawnsA = this.spawnPositions.team_a.length > 0
        ? this.spawnPositions.team_a
        : [{ x: state.self.spawn.x, y: state.self.spawn.y }];
      const spawnsB = this.spawnPositions.team_b.length > 0
        ? this.spawnPositions.team_b
        : [{ x: state.enemy.spawn.x, y: state.enemy.spawn.y }];
      for (const sp of spawnsA) {
        drawSpawn(sp.x, sp.y, "team_a");
      }
      for (const sp of spawnsB) {
        drawSpawn(sp.x, sp.y, "team_b");
      }
    }
  }

  /**
   * V3: 多坦克视野圈 — 遍历所有存活坦克
   */
  private drawVisionCirclesMulti(state: BattleState, cell: number, isMulti: boolean): void {
    const { ctx } = this;
    const visionRange = TANK_STATS.vision_range;
    const attackRange = TANK_STATS.range;

    const drawCircles = (pos: { x: number; y: number }, color: string) => {
      const cx = pos.x * cell + cell / 2;
      const cy = pos.y * cell + cell / 2;
      // V14: 外圈视野范围 — 实线 + 10% 内部填充
      ctx.save();
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.06;
      ctx.beginPath();
      ctx.arc(cx, cy, visionRange * cell, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.18;
      ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.restore();
      // V14: 内圈攻击范围 — 实线更明显
      ctx.save();
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.05;
      ctx.beginPath();
      ctx.arc(cx, cy, attackRange * cell, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.35;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.restore();
    };

    if (isMulti && state.team_a && state.team_b) {
      for (const tank of state.team_a.tanks) {
        if (!tank.is_alive) continue;
        drawCircles(tank.position, this.getColor("teamA"));
      }
      for (const tank of state.team_b.tanks) {
        if (!tank.is_alive) continue;
        drawCircles(tank.position, this.getColor("teamB"));
      }
    } else {
      // 1v1 fallback
      if (state.self.is_alive) drawCircles(state.self.position, this.getColor("teamA"));
      if (state.enemy.is_alive) drawCircles(state.enemy.position, this.getColor("teamB"));
    }
  }

  private drawTank(
    pos: Position,
    team: "team_a" | "team_b",
    hp: number,
    maxHp: number,
    isAlive: boolean,
    lastAction: BotAction | undefined,
  ) {
    const { ctx, opts } = this;
    const cell = opts.cellSize;
    const px = pos.x * cell;
    const py = pos.y * cell;

    // 击杀白闪 — V3: 检查 deathFlashes 队列中是否有匹配的队伍
    let drawColor: string = team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
    const now = performance.now();
    this.deathFlashes = this.deathFlashes.filter((f) => {
      if (f.team !== team) return true;
      const elapsed = now - f.startTime;
      if (elapsed < opts.deathFlashMs) {
        drawColor = "#FFFFFF";
        return true;
      }
      return false; // 过期移除
    });

    // 死亡坦克：墓碑
    if (!isAlive) {
      // FR-022 P0:切 SVG(FR-020 §2.3 tank-dead + §3.4 tombstone-glow)
      // tombstone-glow 是 currentColor 单色元素,死哪队用哪队色(双调警告)
      if (svgLoader.has("tank-dead")) {
        svgLoader.draw(ctx, "tank-dead", { x: px, y: py, dw: cell, dh: cell });
        if (svgLoader.has("tombstone-glow")) {
          const glowColor = team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
          svgLoader.draw(ctx, "tombstone-glow", {
            x: px,
            y: py,
            dw: cell,
            dh: cell,
            color: glowColor,
          });
        }
      } else {
        // fallback:原 hardcode X 图案
        ctx.fillStyle = "#3A4050";
        ctx.fillRect(px + cell / 3, py + cell / 3, cell / 3, cell / 3);
        ctx.strokeStyle = this.getColor("textDim");
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(px + cell / 3, py + cell / 3);
        ctx.lineTo(px + (2 * cell) / 3, py + (2 * cell) / 3);
        ctx.moveTo(px + (2 * cell) / 3, py + cell / 3);
        ctx.lineTo(px + cell / 3, py + (2 * cell) / 3);
        ctx.stroke();
      }
      return;
    }

    // ===== FR-021 改造：混合渲染（SVG 主体 + SVG HP 条，HP 3 档）=====
    // 优先用 svgLoader，已加载则走 SVG；未加载则 fallback 到原 hardcode
    const tankSvgKey: SvgKey = team === "team_a" ? "tank-team-a" : "tank-team-b";
    if (svgLoader.has(tankSvgKey)) {
      // 用 SVG 主体（FR-020 §2.1/2.2 锐角方块 + 朝向短线）
      svgLoader.draw(ctx, tankSvgKey, { x: px, y: py, dw: cell, dh: cell });

      // HP 条（FR-020 §5.1-5.3 3 档色）
      if (opts.showHpBar) {
        const hpRatio = hp / maxHp;
        const hpSvgKey: SvgKey =
          hpRatio > 0.66 ? "hp-bar-high" : hpRatio > 0.33 ? "hp-bar-mid" : "hp-bar-low";
        if (svgLoader.has(hpSvgKey)) {
          svgLoader.draw(ctx, hpSvgKey, { x: px, y: py - 6, dw: cell, dh: 4 });
        } else {
          // HP SVG 未加载，fallback 原 hardcode
          this.drawHpBarFallback(px, py, cell, hp, maxHp, team);
        }
      }
    } else {
      // SVG 主体未加载，fallback 原 hardcode（圆 + 朝向线 + HP 条）
      this.drawTankFallback(px, py, cell, drawColor, lastAction, hp, maxHp, team);
    }

    // P2 删除:之前 9px YOU/ENEMY 文字与 callout-a-base 内文字双层冗余,保留 callout-a-base 即可
  }

  /**
   * V3: 遍历所有 hit 事件绘制命中爆点动效
   * 5 tick 衰减 + scale 1.0→2.0 放大
   */
  private drawAllHits(tick: number, cell: number): void {
    if (this.hitEvents.length === 0) return;
    if (!svgLoader.has("hit-burst")) return;
    const { ctx } = this;

    this.hitEvents = this.hitEvents.filter((hit) => {
      const elapsed = tick - hit.tick;
      if (elapsed < 0 || elapsed >= 5) return false;

      const alpha = 1 - elapsed / 5;
      const scale = 1 + elapsed * 0.2;
      const size = cell * scale;
      const x = hit.target.x * cell + (cell - size) / 2;
      const y = hit.target.y * cell + (cell - size) / 2;
      const color = hit.targetTeam === "team_a" ? this.getColor("teamA") : this.getColor("teamB");

      ctx.save();
      ctx.globalAlpha = alpha;
      svgLoader.draw(ctx, "hit-burst", { x, y, dw: size, dh: size, color });
      ctx.restore();
      return true;
    });
  }

  /**
   * FR-021 fallback：SVG 主体未加载时用原 hardcode（圆 + 朝向线）
   * 保留作 blast-radius 兜底
   */
  private drawTankFallback(
    px: number, py: number, cell: number, drawColor: string,
    lastAction: BotAction | undefined, hp: number, maxHp: number,
    team: "team_a" | "team_b",
  ) {
    const { ctx, opts } = this;
    ctx.fillStyle = drawColor;
    ctx.beginPath();
    ctx.arc(px + cell / 2, py + cell / 2, cell / 2.5, 0, Math.PI * 2);
    ctx.fill();

    const dir = this.getFacing(lastAction);
    ctx.strokeStyle = this.getColor("bg");
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px + cell / 2, py + cell / 2);
    const tx = px + cell / 2 + Math.cos(dir) * (cell / 2);
    const ty = py + cell / 2 + Math.sin(dir) * (cell / 2);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    if (opts.showHpBar) {
      this.drawHpBarFallback(px, py, cell, hp, maxHp, team);
    }
  }

  /**
   * FR-022 P2:Callout 装饰层
   *   - callout-a-base / callout-b-base:绑 tank 头顶(120×32 底框 + 内部填 YOU/ENEMY + HP)
   *   - callout-midline:画在 (12, 12) 中央(中线菱形)
   *   - callout-spawn-a / spawn-b:画在 4 个 spawn 位置
   *   - callout-supply:画在 24 个 cover 位置
   * 任一 SVG 未加载时跳过(屏 4 之前完全无 callout 视觉)
   */
  private drawCallouts(state: BattleState, cell: number): void {
    const { ctx } = this;

    // 8.1 callout-a-base / b-base(玩家 / AI tank 头顶)
    if (svgLoader.has("callout-a-base") && svgLoader.has("callout-b-base")) {
      const baseW = 120;
      const baseH = 32;
      // 玩家 base:tank 头顶上方
      const aX = state.self.position.x * cell + (cell - baseW) / 2;
      const aY = state.self.position.y * cell - baseH - 6;
      svgLoader.draw(ctx, "callout-a-base", {
        x: aX,
        y: aY,
        dw: baseW,
        dh: baseH,
        color: this.getColor("teamA"),
      });
      // 内部填 YOU 文字 + HP 数字
      this.fillCalloutText(aX, aY, baseW, baseH, "YOU", state.self.health, state.self.max_health, "team_a");

      // AI base
      const bX = state.enemy.position.x * cell + (cell - baseW) / 2;
      const bY = state.enemy.position.y * cell - baseH - 6;
      svgLoader.draw(ctx, "callout-b-base", {
        x: bX,
        y: bY,
        dw: baseW,
        dh: baseH,
        color: this.getColor("teamB"),
      });
      this.fillCalloutText(bX, bY, baseW, baseH, "ENEMY", state.enemy.health, state.enemy.max_health, "team_b");
    }

    // 8.2 callout-midline 已删除(纯装饰无玩法意义),直接进 8.3

    // 8.3 callout-spawn-a / spawn-b(简化为 2 个,从 state.spawn 读坐标)
    // 8.3 callout-spawn-a / spawn-b(简化为每队 1 个,位置居中于 2 个 spawn tile)
    if (svgLoader.has("callout-spawn-a") && svgLoader.has("callout-spawn-b")) {
      const spawnW = 32;
      const spawnH = 24;
      // team_a: 画在 spawn 左侧（居中于 self.spawn.y 和 self.spawn.y+1）
      const aY = (state.self.spawn.y + 0.5) * cell + cell / 2;
      svgLoader.draw(ctx, "callout-spawn-a", {
        x: state.self.spawn.x * cell - spawnW - 4,
        y: aY - spawnH / 2,
        dw: spawnW,
        dh: spawnH,
        color: this.getColor("teamA"),
      });
      // team_b: 画在 spawn 右侧
      const bY = (state.enemy.spawn.y + 0.5) * cell + cell / 2;
      svgLoader.draw(ctx, "callout-spawn-b", {
        x: state.enemy.spawn.x * cell + cell + 4,
        y: bY - spawnH / 2,
        dw: spawnW,
        dh: spawnH,
        color: this.getColor("teamB"),
      });
    }

    // 8.4 callout-supply(24 个 cover 位置)
    if (this.coverPositions.length > 0) {
      const amber = this.getColor("fire"); // #FFA630 / #FCD34D
      for (const [cx, cy] of this.coverPositions) {
        if (svgLoader.has("callout-supply")) {
          const sx = cx * cell + (cell - 32) / 2;
          const sy = cy * cell + (cell - 16) / 2;
          svgLoader.draw(ctx, "callout-supply", { x: sx, y: sy, dw: 32, dh: 16, color: amber });
        } else {
          // hardcode fallback: 六边形 polygon (#FCD34D)
          const r = cell * 0.35;
          const px2 = cx * cell + cell / 2;
          const py2 = cy * cell + cell / 2;
          ctx.beginPath();
          for (let k = 0; k < 6; k++) {
            const angle = (Math.PI / 3) * k - Math.PI / 6;
            const vx = px2 + r * Math.cos(angle);
            const vy = py2 + r * Math.sin(angle);
            k === 0 ? ctx.moveTo(vx, vy) : ctx.lineTo(vx, vy);
          }
          ctx.closePath();
          ctx.strokeStyle = "#FCD34D";
          ctx.lineWidth = 1;
          ctx.stroke();
          // 中心小圆点
          ctx.fillStyle = "#FCD34D";
          ctx.globalAlpha = 0.5;
          ctx.beginPath();
          ctx.arc(px2, py2, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
    }
  } // end drawCallouts

  /**
   * FR-022 P2:在 callout-a-base / b-base SVG 内部填 YOU/ENEMY + HP 文字
   * 文字与 SVG 边框对齐(左侧 3px 竖条之后)
   */
  private fillCalloutText(
    x: number, y: number, w: number, h: number,
    label: string, hp: number, maxHp: number, team: "team_a" | "team_b",
  ): void {
    const { ctx } = this;
    const teamColor = team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
    ctx.fillStyle = teamColor;
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + 8, y + h / 2);
    // HP 在右侧
    ctx.fillStyle = this.getColor("text");
    ctx.font = "11px monospace";
    ctx.textAlign = "right";
    ctx.fillText(`${Math.max(0, hp)}/${maxHp}`, x + w - 8, y + h / 2);
  }

  /**
   * FR-022 P2:HUD 4 角 L 形 tactical 角标
   * Ardot 终稿颜色: TL/BL = #4EC9B0(战术青), TR/BR = #F25C54(签名红)
   * SVG 未加载时用 hardcode Canvas Path 画 L 形
   */
  private drawCorners(width: number, height: number): void {
    const { ctx } = this;
    const S = 14;  // L臂长
    const T = 2;   // 笔触宽度
    const cyanColor = this.getColor("teamA");  // #4EC9B0
    const redColor = this.getColor("teamB");   // #F25C54

    const corners: Array<{ svgKey: string; x: number; y: number; color: string; flipX: boolean; flipY: boolean }> = [
      { svgKey: "hud-corner-tl", x: 0,           y: 0,            color: cyanColor, flipX: false, flipY: false },
      { svgKey: "hud-corner-tr", x: width - S*1.5, y: 0,           color: redColor,  flipX: true,  flipY: false },
      { svgKey: "hud-corner-bl", x: 0,           y: height - S*1.5, color: cyanColor, flipX: false, flipY: true  },
      { svgKey: "hud-corner-br", x: width - S*1.5, y: height - S*1.5, color: redColor, flipX: true,  flipY: true  },
    ];

    for (const c of corners) {
      if (svgLoader.has(c.svgKey as SvgKey)) {
        svgLoader.draw(ctx, c.svgKey as SvgKey, { x: c.x, y: c.y, dw: S * 1.5, dh: S * 1.5, color: c.color });
      } else {
        // hardcode L 形: 2 条正交线
        ctx.strokeStyle = c.color;
        ctx.lineWidth = T;
        ctx.beginPath();
        const ox = c.flipX ? c.x + S * 1.5 : c.x;
        const oy = c.flipY ? c.y + S * 1.5 : c.y;
        const dx = c.flipX ? -1 : 1;
        const dy = c.flipY ? -1 : 1;
        ctx.moveTo(ox, oy + dy * S);
        ctx.lineTo(ox, oy);
        ctx.lineTo(ox + dx * S, oy);
        ctx.stroke();
      }
    }
  }

  /**
   * FR-021 fallback：HP SVG 未加载时用原 hardcode
   */
  private drawHpBarFallback(
    px: number, py: number, cell: number, hp: number, maxHp: number,
    team: "team_a" | "team_b",
  ) {
    const { ctx } = this;
    const barW = cell - 4;
    const barH = 3;
    const bx = px + 2;
    const by = py - 5;
    ctx.fillStyle = "#2A3540";
    ctx.fillRect(bx, by, barW, barH);
    ctx.fillStyle = team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
    ctx.fillRect(bx, by, (barW * hp) / maxHp, barH);
  }

  private getFacing(action: BotAction | undefined): number {
    if (!action) return 0;
    if (action.type === "move") {
      const target = action.target;
      return Math.atan2(target.y, target.x);
    }
    if (action.type === "shoot") {
      return 0;
    }
    return 0;
  }

  /**
   * V3: 遍历所有 shoot 事件绘制攻击线
   * 8 tick 内画直线+箭头, alpha 渐出
   */
  private drawAllActionLines(tick: number, cell: number): void {
    if (this.shootLines.length === 0) return;
    const { ctx } = this;

    this.shootLines = this.shootLines.filter((line) => {
      const elapsed = tick - line.tick;
      if (elapsed < 0 || elapsed >= 8) return false;

      const alpha = 1 - elapsed / 8;
      const color = line.team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
      const fx = line.from.x * cell + cell / 2;
      const fy = line.from.y * cell + cell / 2;
      const tx = line.to.x * cell + cell / 2;
      const ty = line.to.y * cell + cell / 2;

      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;         // V14: 1.5→3px 加粗攻击线
      ctx.globalAlpha = alpha * 0.9;
      ctx.setLineDash([6, 4]);   // V14: 4,3→6,4 间距
      ctx.beginPath();
      ctx.moveTo(fx, fy);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      // V14: 箭头增大 50%
      ctx.setLineDash([]);
      const angle = Math.atan2(ty - fy, tx - fx);
      const arrowLen = 12; // V14: 8→12 箭头更大
      ctx.lineWidth = 2;   // V14: 1.5→2
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx - arrowLen * Math.cos(angle - 0.4), ty - arrowLen * Math.sin(angle - 0.4));
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx - arrowLen * Math.cos(angle + 0.4), ty - arrowLen * Math.sin(angle + 0.4));
      ctx.stroke();
      ctx.restore();
      return true;
    });
  }

  // == V14: 动效绘制方法 ==

  /** 屏幕震动 — 受击时 Canvas 位移 2-4px 随机方向 */
  private applyShake(_ctx: CanvasRenderingContext2D, _mapW: number, _mapH: number, _cell: number): void {
    if (!this.shake.active) return;
    const elapsed = this.rAFTick - this.shake.frameStart;
    if (elapsed >= this.shake.duration) { this.shake.active = false; return; }
    const progress = 1 - elapsed / this.shake.duration;
    const intensity = this.shake.intensity * progress;
    const angle = (elapsed * 1.7) % (Math.PI * 2); // 伪随机方向
    const ox = Math.cos(angle) * intensity;
    const oy = Math.sin(angle) * intensity;
    _ctx.translate(ox, oy);
  }

  /** 伤害数字 — 等宽字体 [DMG-N] 上浮淡出 */
  private drawDamageNumbers(cell: number): void {
    const { ctx } = this;
    for (const d of this.damageNumbers) {
      const elapsed = this.rAFTick - d.frameStart;
      const progress = elapsed / d.duration;
      const px = d.pos.x * cell + cell / 2;
      const py = d.pos.y * cell + cell / 2 - progress * cell * 1.5;
      const alpha = 1 - progress;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.font = `bold 14px monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const color = d.team === "team_a" ? this.getColor("teamB") : this.getColor("teamA");
      ctx.fillStyle = color;
      ctx.fillText(`[DMG-${d.value}]`, px, py);
      ctx.restore();
    }
  }

  /** 弹丸飞行 — 菱形弹丸沿轨迹移动 + 虚线尾迹 */
  private drawProjectiles(_tick: number, cell: number): void {
    const { ctx } = this;
    for (const p of this.projectiles) {
      const elapsed = this.rAFTick - p.tickStart;
      const progress = Math.min(1, elapsed / p.duration);
      const cx = p.from.x * cell + cell / 2 + (p.to.x - p.from.x) * cell * progress;
      const cy = p.from.y * cell + cell / 2 + (p.to.y - p.from.y) * cell * progress;
      const color = p.team === "team_a" ? this.getColor("teamA") : this.getColor("teamB");
      const size = 6;
      // 尾迹（虚线指向飞行方向）
      const angle = Math.atan2(p.to.y - p.from.y, p.to.x - p.from.x);
      ctx.save();
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx - Math.cos(angle) * cell * 1.2, cy - Math.sin(angle) * cell * 1.2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      // 菱形弹丸
      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(cx, cy - size);
      ctx.lineTo(cx + size * 0.6, cy);
      ctx.lineTo(cx, cy + size);
      ctx.lineTo(cx - size * 0.6, cy);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  /** 锐角碎片粒子 — 命中/击杀时飞出 */
  private drawFragments(): void {
    const { ctx } = this;
    for (const f of this.fragments) {
      f.x += f.vx;
      f.y += f.vy;
      f.vy += 0.15; // 重力
      f.alpha -= 0.08;
      f.life--;
      ctx.save();
      ctx.globalAlpha = Math.max(0, f.alpha);
      ctx.fillStyle = this.getColor("fire");
      ctx.beginPath();
      // 三角碎片
      const half = f.size / 2;
      ctx.moveTo(f.x, f.y - half);
      ctx.lineTo(f.x + half * 0.6, f.y + half);
      ctx.lineTo(f.x - half * 0.6, f.y + half);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

}
